import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import RateLimitNotification from '../components/RateLimitNotification';
import { 
  MessageCircle, 
  Send, 
  Users, 
  X,
  Home,
  Sparkles,
  Smile,
  ArrowLeft
} from 'lucide-react';
import { eventBus } from '../utils/eventBus';
import EmojiPicker from 'emoji-picker-react';
import { useTranslation } from 'react-i18next';
import {
  getReceivedReactions,
  startChatFromReaction,
  markReactionsSeen,
  REACTION_BY_KEY,
  ENABLE_PASSPORT_REACTIONS,
  type ReceivedReaction,
} from '../api/passportReactions';
import PassportViewer from '../components/Passport/PassportViewer';
import PassportCover from '../components/Passport/PassportCover';
import { getCvPassport, getCvPublicProfile, type PassportStation } from '../api/passport';

// Types
interface User {
  id: string;
  token: string;
  name: string;
  subscription_type: string;
  is_recruiter: boolean;
  icon?: React.ReactNode; // Optional to prevent re-render loop
  color?: string; // Optional to prevent re-render loop
}

interface Conversation {
  id: string;
  participants: Array<{
    user_id: string;
    cv_id?: string | null;
  }>;
  topic: string;
  subject?: string;  // Privacy-friendly display
  last_message_preview: string;
  last_message_at: number;  // Unix timestamp
  unread_count: number;
  last_message_sender_id?: string;  // UUID of user who sent last message
  is_recruiter_reach_out?: boolean;  // True if started from recruiter reach-out
  salary_range?: string;  // Salary range for recruiter reach-outs
  nice_to_know?: string;  // Optional additional info for recruiter reach-outs
}

interface RecruiterInfo {
  id: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  recruiter_background?: string;
  profile_image_url?: string;
  is_recruiter: boolean;
}

interface Message {
  id: string;
  conversation_id: string;
  sender: {
    user_id: string;
    display_name: string;  // PRIVACY SAFE: "Explorer" or "Recruiter"
  };
  content: string;
  message_type: string;
  created_at: number;  // Unix timestamp
  is_read: boolean;
}



const Messages: React.FC = () => {
  const { user, apiCall: customApiCall, isSignedIn } = useAuth();
  const { t } = useTranslation('messages');
  const navigate = useNavigate();

  // Format time relative (like "2h ago")
  const formatTime = (timestamp?: number) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return t('time.justNow');
    if (diffMins < 60) return t('time.minutesAgo', { minutes: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('time.hoursAgo', { hours: diffHours });
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return t('time.daysAgo', { days: diffDays });
    return date.toLocaleDateString();
  }; // Custom hook with auto-retry
  // Note: Real-time updates come via EventBus from InboxWidget SSE connection
  
  // State
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  // Emit total unread whenever conversations change
  useEffect(() => {
    const totalUnread = conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0);
    eventBus.emit('messaging:total_unread', { total_unread: totalUnread });
  }, [conversations]);
  const [newMessage, setNewMessage] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [conversationFilter, setConversationFilter] = useState<'all' | 'recruiter' | 'normal'>('all');
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  // Sparks: passport reactions received → start a free chat
  const [sparks, setSparks] = useState<ReceivedReaction[]>([]);
  const [startingSparkId, setStartingSparkId] = useState<number | null>(null);
  const [viewingPassportCvId, setViewingPassportCvId] = useState<string | null>(null);
  // Inbox passport covers: anonymized passport bits per counterpart cv_id,
  // loaded lazily + cached so the contact list can render passport covers.
  const [passportCache, setPassportCache] = useState<Record<string, {
    realTalk: string | null;
    tags: string[];
    stations: PassportStation[];
    thumbnail: string | null;
    loaded: boolean;
  }>>({});
  const passportFetchedRef = useRef<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // Fetch (once) the anonymized passport bits for a counterpart's cv_id.
  const ensurePassport = async (cvId: string) => {
    if (passportFetchedRef.current.has(cvId)) return;
    passportFetchedRef.current.add(cvId);
    const imagesUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/cv/${cvId}/images`;
    try {
      const [pass, pub, imgData] = await Promise.all([
        getCvPassport(customApiCall, cvId).catch(() => null),
        getCvPublicProfile(customApiCall, cvId).catch(() => null),
        customApiCall(imagesUrl).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      setPassportCache((prev) => ({
        ...prev,
        [cvId]: {
          realTalk: pub?.real_talk_sentence ?? null,
          tags: pub?.real_talk_tags ?? [],
          stations: pass?.stations ?? [],
          thumbnail: imgData?.images?.[0] ?? null,
          loaded: true,
        },
      }));
    } catch {
      setPassportCache((prev) => ({
        ...prev,
        [cvId]: { realTalk: null, tags: [], stations: [], thumbnail: null, loaded: true },
      }));
    }
  };

  // Prefetch passport covers for explorer↔explorer conversations.
  useEffect(() => {
    if (!ENABLE_PASSPORT_REACTIONS || !currentUser || currentUser.is_recruiter) return;
    conversations.forEach((c) => {
      if (c.is_recruiter_reach_out) return;
      const cvId = c.participants?.find((p) => p.cv_id)?.cv_id;
      if (cvId && !passportFetchedRef.current.has(cvId)) ensurePassport(cvId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, currentUser]);
  
  // Recruiter Info Modal State
  const [showRecruiterInfo, setShowRecruiterInfo] = useState(false);
  const [recruiterInfo, setRecruiterInfo] = useState<RecruiterInfo | null>(null);
  const [loadingRecruiterInfo, setLoadingRecruiterInfo] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // API Base URL from environment variable
  const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1`;
  
  // Wrapper to make custom apiCall compatible with existing code
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await customApiCall(fullUrl, options);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'API Error' }));
      throw new Error(error.detail || 'API Error');
    }
    
    return response.json();
  };

  // Scroll to bottom of messages (only within chat container, not globally)
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Load user profile when signed in (with guard to prevent duplicate calls)
  useEffect(() => {
    const loadProfileWhenReady = async () => {
      if (!isSignedIn || !user) {
        return;
      }

      // Avoid duplicate attempts
      if (currentUser || hasAttemptedLoad) {
        return;
      }

      setHasAttemptedLoad(true);
      loadUserProfile();
    };

    loadProfileWhenReady();
  }, [isSignedIn, user?.userId]);

  // Load user profile from Profile Service
  const loadUserProfile = async () => {
    if (!user?.userId) return;
    
    try {
      console.debug('🔗 Messages: Loading user profile via Gateway');
      const profile = await apiCall(`/profiles/${user.userId}`);
      
      // ❌ Redirect recruiters to recruiter inbox (silent redirect, no toast)
      if (profile.is_recruiter) {
        navigate('/recruiter-inbox');
        return;
      }
      
      setCurrentUser({
        id: profile.id, // Use UUID instead of Clerk ID
        token: '', // Will be handled by getToken()
        name: profile.first_name || 'User',
        subscription_type: profile.subscription_type,
        is_recruiter: profile.is_recruiter,
        // Removed icon and color to prevent re-render loop
      });
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Fallback to basic user info
      setCurrentUser({
        id: user.userId, // Keep Clerk ID as fallback
        token: '',
        name: 'User',
        subscription_type: 'explorer_free',
        is_recruiter: false,
        // Removed icon and color to prevent re-render loop
      });
    }
  };

  useEffect(() => {
    // Use setTimeout to ensure DOM is updated before scrolling
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [messages]);

  // Fetch user limits and conversations when user changes
  useEffect(() => {
    if (currentUser) {
      loadUserConversations();
      loadSparks();
    }
  }, [currentUser]);

  // Load received passport reactions (only those not yet turned into a chat).
  const loadSparks = async () => {
    if (!ENABLE_PASSPORT_REACTIONS) return;
    try {
      const received = await getReceivedReactions(customApiCall);
      setSparks(received.filter((r) => !r.conversation_id));
    } catch (e) {
      console.debug('Failed to load sparks:', e);
    }
  };

  // Refresh sparks when a new reaction arrives via SSE (InboxWidget fan-out).
  useEffect(() => {
    const off = eventBus.on('messaging:passport_reaction', () => {
      if (currentUser) loadSparks();
    });
    return () => off();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Mark sparks as seen once the user is on the Messages page.
  useEffect(() => {
    if (currentUser && ENABLE_PASSPORT_REACTIONS) {
      markReactionsSeen(customApiCall).catch(() => {});
      eventBus.emit('messaging:sparks_seen', {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Start a FREE chat from a received reaction, then open the conversation.
  const handleStartSparkChat = async (spark: ReceivedReaction) => {
    if (startingSparkId) return;
    setStartingSparkId(spark.id);
    try {
      const { conversation_id } = await startChatFromReaction(customApiCall, spark.id);
      setSparks((prev) => prev.filter((s) => s.id !== spark.id));
      await loadUserConversations(true);
      await loadMessages(conversation_id);
      setSelectedConversation((prev) => {
        // Prefer the freshly loaded conversation object if present.
        return { id: conversation_id, participants: [], topic: '✨ Sparked chat', subject: '✨ Sparked chat', last_message_preview: '', last_message_at: Math.floor(Date.now() / 1000), unread_count: 0 } as Conversation;
      });
    } catch (e) {
      console.error('Failed to start spark chat:', e);
      showToast('Could not start chat', 'error');
    } finally {
      setStartingSparkId(null);
    }
  };

  // Refresh conversations when SSE reconnects (to catch missed messages) - silent, no loading spinner
  useEffect(() => {
    const unsubscribe = eventBus.on('messaging:refresh_needed', () => {
      console.debug('🔄 Messages: Refreshing conversations after SSE reconnect (silent)');
      if (currentUser) {
        loadUserConversations(true, true); // preserve selection, silent (no loading UI)
        if (selectedConversation) {
          loadMessages(selectedConversation.id);
        }
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, selectedConversation]);

  // Subscribe to SSE new message events to update UI in real-time
  useEffect(() => {
    const unsubscribe = eventBus.on('messaging:new_message', (evt: any) => {
      const { conversation_id, message } = evt || {};
      if (!conversation_id || !message) return;
      
      // Extract conversation metadata from message payload
      const { conversation_subject, is_recruiter_reach_out, salary_range, nice_to_know } = message || {};

      // Check if conversation exists and update
      setConversations(prev => {
        const existingIndex = prev.findIndex(c => c.id === conversation_id);
        
        if (existingIndex === -1) {
          // NEW CONVERSATION - create preview from SSE data (privacy-friendly)
          const newConv: Conversation = {
            id: conversation_id,
            participants: [
              {
                user_id: message?.sender?.user_id || ''
              }
            ],
            topic: 'New Message',
            subject: conversation_subject || 'Conversation',  // Use subject from SSE event
            is_recruiter_reach_out: is_recruiter_reach_out || false,
            salary_range: salary_range || undefined,
            nice_to_know: nice_to_know || undefined,
            last_message_preview: message?.content || '',
            last_message_at: typeof message?.created_at === 'number' 
              ? message.created_at
              : Math.floor(Date.now() / 1000),
            unread_count: selectedConversation?.id === conversation_id ? 0 : 1,
          };
          
          console.debug('📬 New conversation added to top:', conversation_id, 'subject:', conversation_subject, 'salary:', salary_range);
          // Add to top of list
          return [newConv, ...prev];
        }
        
        // UPDATE EXISTING conversation
        const updated = prev.map(c => {
          if (c.id !== conversation_id) return c;
          const isActive = selectedConversation?.id === conversation_id;
          return {
            ...c,
            unread_count: isActive ? c.unread_count : (c.unread_count || 0) + 1,
            last_message_preview: message.content || c.last_message_preview,
            last_message_at: typeof message.created_at === 'number' 
              ? message.created_at
              : c.last_message_at,
            // Update recruiter-specific fields from SSE event (they don't change but ensure consistency)
            salary_range: salary_range || c.salary_range,
            nice_to_know: nice_to_know || c.nice_to_know,
          };
        });
        
        // Sort by last_message_at (newest first) to move updated conversation to top
        return updated.sort((a, b) => (b.last_message_at || 0) - (a.last_message_at || 0));
      });

      // If user is viewing this conversation, append message live and keep it
      // read on the backend so it doesn't resurface as unread after a reload.
      if (selectedConversation?.id === conversation_id) {
        setMessages(prev => [...prev, message]);
        setTimeout(() => scrollToBottom(), 50);
        apiCall(`/messaging/conversations/${conversation_id}/read`, { method: 'POST' }).catch(() => {});
      }
    });

    return () => {
      unsubscribe();
    };
  }, [selectedConversation]);

  // Fetch recruiter info for a conversation
  const fetchRecruiterInfo = async (conversationId: string) => {
    try {
      setLoadingRecruiterInfo(true);
      const response = await apiCall(`/messaging/conversations/${conversationId}/recruiter-info`);
      setRecruiterInfo(response);
      setShowRecruiterInfo(true);
    } catch (error) {
      console.error('Failed to fetch recruiter info:', error);
      showToast(t('errors.loadRecruiterInfo'), 'error');
    } finally {
      setLoadingRecruiterInfo(false);
    }
  };

    // Load user's conversations
  const loadUserConversations = async (preserveSelection = false, silent = false) => {
    if (!currentUser) return;
    
    console.debug('🔄 Loading conversations for:', currentUser.name, silent ? '(silent refresh)' : '');
    try {
      if (!silent) setConversationsLoading(true);
      const response = await apiCall('/messaging/conversations/');
      console.debug('📋 Conversations response:', response);
      setConversations(response.conversations || []);
      
      // Only clear selection if not preserving it
      if (!preserveSelection) {
        setSelectedConversation(null);
        setMessages([]);
      }
      
      console.debug(`✅ Loaded ${response.conversations?.length || 0} conversations for ${currentUser.name}`);

      if (response.conversations?.length > 0) {
        console.debug(`✅ Found ${response.conversations.length} conversations`);
      } else {
        console.debug('⚠️ No conversations found');
      }
    } catch (error: any) {
      console.error('❌ Failed to load conversations:', error);
      setConversations([]);
      
      // Show user-friendly error
      if (error.message && error.message.includes('429')) {
        showToast(t('errors.tooManyRequests'), 'error', 6000);
      } else if (error.message && error.message.includes('503')) {
        showToast(t('errors.serviceUnavailable'), 'error');
      } else {
        showToast(t('errors.loadConversationsFailed'), 'error');
      }
    } finally {
      if (!silent) setConversationsLoading(false);
    }
  };

  // Send message
  const handleEmojiClick = (emojiData: any) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const messageContent = newMessage;
    setNewMessage('');
    setSendingMessage(true);

    try {
      const message = await apiCall(`/messaging/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          content: messageContent
        }),
      });

      setMessages(prev => [...prev, message]);
      console.debug('✅ Message sent successfully');
      
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Failed to send message:', error);

    } finally {
      setSendingMessage(false);
    }
  };

  // Get messages for conversation
  const loadMessages = async (conversationId: string) => {
    setIsLoading(true);
    try {
      const response = await apiCall(`/messaging/conversations/${conversationId}/messages?limit=100`);
      // Reverse messages to show chronological order (oldest first)
      setMessages(response.messages.reverse());

      // Optimistically zero unread for this conversation in UI
      setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, unread_count: 0 } : c));
      // Best-effort notify backend (optional; backend may already track reads)
      try {
        await apiCall(`/messaging/conversations/${conversationId}/read`, { method: 'POST' });
      } catch (e) {
        // non-fatal
      }
      
      // Scroll to bottom after loading messages
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Failed to load messages:', error);

      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };





  // Show loading if user not loaded yet
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-3 sm:p-4 border-b border-slate-100 relative overflow-hidden">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/80 to-transparent z-10" />
              <div className="h-4 w-28 sm:w-36 bg-pink-100 rounded-full animate-pulse" />
            </div>
            <div className="space-y-0">
              {[
                { w1: 'w-2/3', w2: 'w-full', w3: 'w-1/3', accent: true },
                { w1: 'w-1/2', w2: 'w-4/5', w3: 'w-2/5', accent: false },
                { w1: 'w-3/4', w2: 'w-full', w3: 'w-1/4', accent: true },
                { w1: 'w-1/2', w2: 'w-2/3', w3: 'w-1/3', accent: false },
                { w1: 'w-2/3', w2: 'w-5/6', w3: 'w-2/5', accent: true },
                { w1: 'w-3/5', w2: 'w-full', w3: 'w-1/4', accent: false },
                { w1: 'w-1/2', w2: 'w-3/4', w3: 'w-1/3', accent: true },
                { w1: 'w-2/3', w2: 'w-full', w3: 'w-2/5', accent: false },
              ].map((widths, i) => (
                <div key={i} className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-100 relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/80 to-transparent z-10" />
                  <div className="space-y-2">
                    <div className={`h-3 ${widths.w1} ${widths.accent ? 'bg-pink-100' : 'bg-slate-200'} rounded-full animate-pulse`} />
                    <div className={`h-2.5 ${widths.w2} bg-slate-100 rounded-full animate-pulse`} />
                    <div className={`h-2 ${widths.w3} bg-slate-100/70 rounded-full animate-pulse`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Rate Limit Notification */}
      {rateLimitInfo && (
        <RateLimitNotification 
          rateLimitInfo={rateLimitInfo}
          feature="reach_outs"
          onClose={() => setRateLimitInfo(null)}
        />
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-2 sm:py-6">
        <div className="lg:grid lg:grid-cols-12 gap-4 sm:gap-6" style={{ height: 'calc(100vh - 120px)', maxHeight: 'calc(100vh - 120px)' }}>
          
          {/* Left Sidebar - Conversations */}
          <div className={`${selectedConversation ? 'hidden lg:flex' : 'flex'} lg:col-span-4 space-y-2 sm:space-y-3 flex-col overflow-hidden min-h-0 h-full`}>
            
            {/* Conversations List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 flex-1 flex flex-col overflow-hidden min-h-0"
            >
              <div className="flex items-center gap-2 mb-2.5">
                <Users className="w-4 h-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-800">{t('conversations.title')} ({conversations.length})</h3>
              </div>

              {/* Sparks — passport reactions you received (start a free chat) */}
              {sparks.length > 0 && (
                <div className="mb-2.5 rounded-xl border border-pink-200 bg-gradient-to-r from-pink-50 to-rose-50 p-2.5">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles className="w-3.5 h-3.5 text-pink-500" />
                    <span className="text-xs font-bold text-pink-700">Sparks</span>
                    <span className="text-[10px] text-pink-400 font-medium">· someone vibed your passport</span>
                  </div>
                  <div className="space-y-1.5">
                    {sparks.map((spark) => {
                      const def = REACTION_BY_KEY[spark.reaction];
                      return (
                        <div
                          key={spark.id}
                          className="flex items-center justify-between gap-2 bg-white rounded-lg border border-pink-100 px-2.5 py-1.5"
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-base leading-none">{def?.emoji || '✨'}</span>
                            <span className="text-xs font-medium text-slate-600 truncate">
                              An Explorer reacted{def?.label ? ` "${def.label}"` : ''}
                            </span>
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-1.5">
                            {spark.reactor_cv_id && (
                              <button
                                onClick={() => setViewingPassportCvId(spark.reactor_cv_id)}
                                className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full text-pink-600 bg-white border border-pink-200 hover:bg-pink-50 transition-all"
                              >
                                <span>View passport</span>
                              </button>
                            )}
                            <button
                              onClick={() => handleStartSparkChat(spark)}
                              disabled={startingSparkId === spark.id}
                              className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 disabled:opacity-60 transition-all shadow-sm"
                            >
                              {startingSparkId === spark.id ? (
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Sparkles className="w-3 h-3" />
                              )}
                              <span>Free chat</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Filter Tabs */}
              <div className="flex gap-1.5 mb-2.5">
                <button
                  onClick={() => setConversationFilter('all')}
                  className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                    conversationFilter === 'all'
                      ? 'bg-slate-800 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  ✨ {t('filters.all')}
                </button>
                <button
                  onClick={() => setConversationFilter('normal')}
                  className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                    conversationFilter === 'normal'
                      ? 'bg-slate-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {t('filters.explorers')}
                </button>
                <button
                  onClick={() => setConversationFilter('recruiter')}
                  className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                    conversationFilter === 'recruiter'
                      ? 'bg-slate-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {t('filters.recruiters')}
                </button>
              </div>
              
              <div className="space-y-1.5 flex-1 overflow-y-auto min-h-0 px-0.5 pt-0.5">
                {conversationsLoading ? (
                  ENABLE_PASSPORT_REACTIONS && !currentUser?.is_recruiter ? (
                    /* Explorers see passport-cover cards, so the skeleton mirrors
                       that shape (CV thumb + stations + footer) instead of the old
                       compact two-line rows. */
                    <div className="space-y-1.5">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden relative">
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/70 to-transparent z-10" />
                          <div className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-16 flex-shrink-0 aspect-[3/4] rounded-lg bg-slate-100 animate-pulse" />
                              <div className="flex-1 min-w-0 space-y-2.5">
                                {[0, 1, 2].map((j) => (
                                  <div key={j} className="flex items-center gap-2.5" style={{ opacity: 1 - j * 0.18 }}>
                                    <div className="w-6 h-6 rounded-full bg-pink-50 animate-pulse flex-shrink-0" />
                                    <div className="h-3 flex-1 bg-slate-200/70 rounded animate-pulse" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          {/* Footer: chat preview (left) + View passport pill (right) */}
                          <div className="px-3.5 py-2.5 border-t border-slate-100 flex items-end justify-between gap-2">
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <div className="h-2.5 w-3/4 bg-slate-100 rounded-full animate-pulse" />
                              <div className="h-2 w-12 bg-slate-100/70 rounded-full animate-pulse" />
                            </div>
                            <div className="h-6 w-24 bg-pink-50 rounded-full animate-pulse flex-shrink-0" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                  <div className="space-y-1 px-1">
                    {[
                      { w1: 'w-2/3', w2: 'w-full', accent: true },
                      { w1: 'w-1/2', w2: 'w-4/5', accent: false },
                      { w1: 'w-3/4', w2: 'w-full', accent: true },
                      { w1: 'w-1/2', w2: 'w-2/3', accent: false },
                      { w1: 'w-2/3', w2: 'w-5/6', accent: true },
                      { w1: 'w-3/5', w2: 'w-full', accent: false },
                      { w1: 'w-1/2', w2: 'w-3/4', accent: true },
                    ].map((widths, i) => (
                      <div key={i} className="p-2 sm:p-2.5 rounded-lg relative overflow-hidden">
                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/80 to-transparent z-10" />
                        <div className="space-y-1.5">
                          <div className={`h-3 ${widths.w1} ${widths.accent ? 'bg-pink-100' : 'bg-slate-200'} rounded-full animate-pulse`} />
                          <div className={`h-2.5 ${widths.w2} bg-slate-100 rounded-full animate-pulse`} />
                        </div>
                      </div>
                    ))}
                  </div>
                  )
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                    <p className="text-sm font-medium text-slate-600">{t('conversations.noConversations')}</p>
                    <p className="text-xs text-slate-400">{t('conversations.noConversationsHint')}</p>
                  </div>
                ) : (
                  conversations
                    .filter(conversation => {
                      if (conversationFilter === 'all') return true;
                      if (conversationFilter === 'recruiter') return conversation.is_recruiter_reach_out === true;
                      if (conversationFilter === 'normal') return conversation.is_recruiter_reach_out !== true;
                      return true;
                    })
                    .map((conversation) => {
                    const selectChat = async () => {
                      try {
                        setLoadingConversationId(conversation.id);
                        setIsLoading(true);
                        // Load messages first, then set conversation
                        await loadMessages(conversation.id);
                        setSelectedConversation(conversation);
                      } catch (error) {
                        console.error('Failed to load conversation:', error);
                        setIsLoading(false);
                      } finally {
                        setLoadingConversationId(null);
                      }
                    };
                    const isSelected = selectedConversation?.id === conversation.id;

                    // Explorer↔explorer chats render as a condensed passport cover
                    // (no header band, no stops/reactions line) with a chat preview
                    // footer — so the inbox reads as a list of contacts, not subjects.
                    const passportCvId = conversation.participants?.find((p) => p.cv_id)?.cv_id;
                    if (ENABLE_PASSPORT_REACTIONS && !currentUser?.is_recruiter && !conversation.is_recruiter_reach_out && passportCvId) {
                      const pp = passportCache[passportCvId];
                      const senderPrefix = conversation.last_message_sender_id === currentUser?.id ? 'You: ' : 'Explorer: ';
                      return (
                        <PassportCover
                          key={conversation.id}
                          hideHeader
                          hideNowBadge
                          thumbnail={pp?.thumbnail}
                          cvPlaceholder={!pp?.thumbnail}
                          loading={!pp?.loaded}
                          stations={pp?.stations}
                          onClick={selectChat}
                          className={isSelected ? 'ring-1 ring-inset ring-pink-300 !border-pink-300 shadow-lg shadow-pink-100/40' : conversation.unread_count > 0 ? '!border-pink-200' : ''}
                          footer={(
                            <div className="flex items-end justify-between gap-2 w-full">
                              <div className="min-w-0 flex-1">
                                {conversation.last_message_preview ? (
                                  <div className="text-[11px] sm:text-xs text-slate-500 line-clamp-1">
                                    <span className="font-medium text-slate-600">{senderPrefix}</span>
                                    <span dangerouslySetInnerHTML={{ __html: conversation.last_message_preview.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-slate-300 italic">{t('conversations.noMessagesYet', 'No messages yet')}</span>
                                )}
                                <div className="text-[10px] text-slate-400 mt-0.5">{formatTime(conversation.last_message_at)}</div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {conversation.unread_count > 0 && (
                                  <span className="bg-red-600 text-white text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-semibold">
                                    {conversation.unread_count}
                                  </span>
                                )}
                                {loadingConversationId === conversation.id ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border border-pink-600 border-t-transparent" />
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setViewingPassportCvId(passportCvId); }}
                                    className="text-[11px] font-semibold text-pink-600 bg-pink-50 hover:bg-pink-100 border border-pink-100 px-2.5 py-1 rounded-full transition-colors whitespace-nowrap"
                                  >
                                    {t('conversations.viewPassport', 'View passport')}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      );
                    }

                    return (
                    <div
                      key={conversation.id}
                      onClick={selectChat}
                      className={`group p-2.5 sm:p-3 rounded-xl cursor-pointer transition-all border ${
                        isSelected
                          ? 'bg-pink-50 border-pink-200 shadow-sm'
                          : conversation.unread_count > 0
                            ? 'bg-white border-pink-200 shadow-sm hover:shadow-md'
                            : 'bg-white border-slate-100 hover:border-pink-200 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Subject Row — for explorer↔explorer chats we surface the
                              counterpart's passport instead of a subject line. */}
                          {(() => {
                            const otherCvId = conversation.participants?.find((p) => p.cv_id)?.cv_id;
                            const showPassportEntry =
                              ENABLE_PASSPORT_REACTIONS && !conversation.is_recruiter_reach_out && !!otherCvId;
                            if (showPassportEntry) {
                              return (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setViewingPassportCvId(otherCvId!); }}
                                  className="flex items-center gap-1 sm:gap-1.5 mb-1 sm:mb-1.5 text-pink-600 hover:text-pink-700"
                                >
                                  <Sparkles className="w-3.5 h-3.5" />
                                  <span className="font-semibold text-xs sm:text-sm truncate">View passport</span>
                                </button>
                              );
                            }
                            return (
                              <div className="flex items-center gap-1 sm:gap-1.5 mb-1 sm:mb-1.5">
                                <span className="text-xs sm:text-sm">📧</span>
                                <div className="font-semibold text-slate-800 text-xs sm:text-sm truncate">
                                  {conversation.subject || 'Conversation'}
                                </div>
                              </div>
                            );
                          })()}
                          
                          {/* Recruiter Reach-out Info */}
                          {conversation.is_recruiter_reach_out && (conversation.salary_range || conversation.nice_to_know) && (
                            <div className="mt-2 space-y-1">
                              {conversation.salary_range && (
                                <div className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded border border-green-200 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1">
                                    <span>💰</span>
                                    <span>{conversation.salary_range}</span>
                                  </div>
                                  
                                  {/* Recruiter Info Button - In Gehaltszeile */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent conversation selection
                                      fetchRecruiterInfo(conversation.id);
                                    }}
                                    disabled={loadingRecruiterInfo}
                                    className="text-xs px-2 py-1 bg-pink-50 text-pink-700 border border-pink-200 rounded-full hover:bg-pink-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md flex-shrink-0"
                                  >
                                    {loadingRecruiterInfo ? '⏳' : '👤'} {t('recruiter.infoButton')}
                                  </button>
                                </div>
                              )}
                              {conversation.nice_to_know && (
                                <div className="text-xs text-yellow-700 bg-yellow-50 px-2 py-1 rounded border border-yellow-200 flex items-start gap-1">
                                  <span className="mt-0.5">✨</span>
                                  <span className="line-clamp-2">{conversation.nice_to_know}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {conversation.last_message_preview && (
                            <div className="text-[10px] sm:text-xs text-slate-500 line-clamp-2 mt-1 sm:mt-1.5">
                              {conversation.last_message_sender_id === currentUser?.id ? (
                                <span className="font-medium text-slate-600">You: </span>
                              ) : (
                                <span className="font-medium text-slate-600">{conversation.is_recruiter_reach_out ? 'Recruiter' : 'Explorer'}: </span>
                              )}
                              <span 
                                dangerouslySetInnerHTML={{ 
                                  __html: conversation.last_message_preview.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                }}
                              />
                            </div>
                          )}
                          
                          <div className="text-[10px] text-slate-400 mt-1.5">
                            {formatTime(conversation.last_message_at)}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {conversation.unread_count > 0 && (
                            <div className="relative inline-flex items-center">
                              <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-pink-600" />
                              <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-red-600 text-white text-[8px] sm:text-[10px] rounded-full min-w-[12px] min-h-[12px] sm:min-w-[14px] sm:min-h-[14px] px-0.5 sm:px-1 flex items-center justify-center font-medium">
                                {conversation.unread_count}
                              </span>
                            </div>
                          )}
                          {loadingConversationId === conversation.id && (
                            <div className="animate-spin rounded-full h-3 w-3 border border-pink-600 border-t-transparent"></div>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </div>

          {/* Right Side - Chat Window */}
          <div className={`${selectedConversation ? 'flex' : 'hidden lg:flex'} lg:col-span-8 flex-col overflow-hidden min-h-0 h-full`}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-slate-100 flex-1 flex flex-col overflow-hidden"
            >
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="px-3 py-2.5 sm:p-4 border-b border-slate-100 flex-shrink-0">
                    <div className="flex items-center gap-2.5 sm:gap-3">
                      {/* Back Button - Mobile & iPad */}
                      <button
                        onClick={() => setSelectedConversation(null)}
                        className="lg:hidden flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 transition-colors flex-shrink-0"
                      >
                        <ArrowLeft className="w-5 h-5 text-slate-600" />
                      </button>
                      
                      {(() => {
                        const headerCvId = selectedConversation.participants?.find((p) => p.cv_id)?.cv_id;
                        const isPassportChat = ENABLE_PASSPORT_REACTIONS && !currentUser?.is_recruiter && !selectedConversation.is_recruiter_reach_out && !!headerCvId;
                        // Explorer↔explorer: surface the passport instead of a subject line.
                        if (isPassportChat) {
                          const realTalk = passportCache[headerCvId!]?.realTalk;
                          return (
                            <>
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white text-sm sm:text-lg flex-shrink-0">
                                📧
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm sm:text-base text-slate-800 truncate">
                                  {t('conversations.explorer', 'Explorer')}
                                </div>
                                {realTalk && (
                                  <div className="text-[11px] sm:text-xs text-slate-400 italic truncate">“{realTalk}”</div>
                                )}
                              </div>
                              <button
                                onClick={() => setViewingPassportCvId(headerCvId!)}
                                className="flex items-center gap-1 text-xs font-semibold text-pink-500 hover:text-pink-600 px-2 py-1.5 rounded-lg hover:bg-pink-50 flex-shrink-0"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">{t('conversations.viewPassport', 'View passport')}</span>
                              </button>
                            </>
                          );
                        }
                        return (
                          <>
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white text-sm sm:text-lg flex-shrink-0">
                              📧
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm sm:text-base text-slate-800 truncate">
                                {selectedConversation.subject || 'Conversation'}
                              </div>

                              {/* Recruiter Reach-Out Details */}
                              {selectedConversation.is_recruiter_reach_out && (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {selectedConversation.salary_range && (
                                    <span className="text-[10px] sm:text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                                      💰 {selectedConversation.salary_range}
                                    </span>
                                  )}
                                  {selectedConversation.nice_to_know && (
                                    <span className="text-[10px] sm:text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 truncate max-w-[200px]">
                                      ✨ {selectedConversation.nice_to_know}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Messages - Scrollable Area */}
                  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 py-3 sm:p-4 space-y-3 relative min-h-0">
                    {/* Loading Overlay */}
                    {isLoading && (
                      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10 px-3">
                        <div className="text-center max-w-[220px]">
                          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-pink-500 mx-auto mb-2 sm:mb-3"></div>
                          <p className="text-xs sm:text-sm text-slate-600 leading-snug">Loading messages...</p>
                        </div>
                      </div>
                    )}

                    {messages.length === 0 && !isLoading ? (
                      (() => {
                        const ebCvId = selectedConversation.participants?.find((p) => p.cv_id)?.cv_id;
                        const isPassportChat = ENABLE_PASSPORT_REACTIONS && !currentUser?.is_recruiter && !selectedConversation.is_recruiter_reach_out && !!ebCvId;
                        if (!isPassportChat) {
                          return (
                            <div className="text-center py-12">
                              <MessageCircle className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                              <p className="text-sm font-medium text-slate-600">No messages yet</p>
                              <p className="text-xs text-slate-400">Start the conversation!</p>
                            </div>
                          );
                        }
                        const pickOpener = (text: string) => {
                          setNewMessage(text);
                          setTimeout(() => composerRef.current?.focus(), 0);
                        };
                        return (
                          <div className="text-center py-8 px-2">
                            <p className="text-sm font-semibold text-slate-700">{t('icebreaker.title')}</p>
                            <p className="text-xs text-slate-400 mb-4">{t('icebreaker.subtitle')}</p>
                            <div className="flex flex-col gap-2 max-w-xs mx-auto">
                              {[t('icebreaker.o1'), t('icebreaker.o2'), t('icebreaker.o3')].map((opener, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => pickOpener(opener)}
                                  className="text-left text-sm px-3.5 py-2.5 rounded-2xl border border-pink-200 bg-pink-50/60 text-slate-700 hover:bg-pink-100 hover:border-pink-300 transition-colors"
                                >
                                  {opener}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      messages.map((message) => {
                        const isCurrentUser = message.sender.user_id === currentUser.id;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] sm:max-w-xs lg:max-w-md px-3 py-2 sm:px-4 sm:py-2.5 rounded-2xl ${
                                isCurrentUser
                                  ? 'bg-gradient-to-br from-pink-500 to-rose-500 text-white'
                                  : 'bg-white border border-slate-200 text-slate-800'
                              }`}
                            >
                              <div 
                                className="text-sm whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ 
                                  __html: message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                }}
                              />
                              <div className={`text-[10px] mt-1 ${isCurrentUser ? 'text-pink-200' : 'text-slate-400'}`}>
                                {new Date(message.created_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input - Fixed at Bottom */}
                  <div className="px-3 py-2.5 sm:p-4 border-t border-slate-100 flex-shrink-0">
                    <div className="flex gap-2 items-stretch relative">
                      <div className="flex-1 relative flex items-center">
                        <textarea
                          ref={composerRef}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={t('input.placeholder')}
                          className="w-full min-h-[40px] px-3 py-2 pr-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-100 transition-colors resize-none"
                          maxLength={2000}
                          rows={1}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                            // Shift+Enter = new line (default behavior)
                          }}
                        />
                        
                        {/* Emoji Button */}
                        <button
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Add emoji"
                        >
                          <Smile className="w-5 h-5 text-gray-500" />
                        </button>

                        {/* Emoji Picker */}
                        {showEmojiPicker && (
                          <div className="absolute bottom-full right-0 mb-2 z-50">
                            <EmojiPicker
                              onEmojiClick={handleEmojiClick}
                              width={350}
                              height={400}
                            />
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={sendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="px-3 sm:px-4 min-h-[40px] bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl hover:shadow-md disabled:bg-slate-200 disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 self-end transition-all"
                      >
                        <Send className="w-4 h-4" />
                        <span className="hidden sm:inline text-sm font-medium">{sendingMessage ? '...' : 'Send'}</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <h3 className="text-base font-medium text-slate-700 mb-1">{t('emptyState.title')}</h3>
                    <p className="text-sm text-slate-500 mb-3">{t('emptyState.subtitle')}</p>
                    <p className="text-xs text-slate-400">{t('emptyState.hint')}</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Recruiter Info Modal */}
      {showRecruiterInfo && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-3 sm:p-4"
          onClick={() => setShowRecruiterInfo(false)}
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-pink-500 to-rose-500 p-4 sm:p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <span className="text-base">👤</span>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold">Recruiter Profile</h3>
                    <p className="text-pink-200 text-xs">Professional Information</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRecruiterInfo(false)}
                  className="text-white/80 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-5">
              {recruiterInfo ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    {recruiterInfo.profile_image_url ? (
                      <img
                        src={recruiterInfo.profile_image_url.startsWith('/') ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}${recruiterInfo.profile_image_url}` : recruiterInfo.profile_image_url}
                        alt={`${recruiterInfo.first_name || 'Recruiter'}`}
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover shadow-md border-2 border-white"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                        <span className="text-white font-bold text-lg">
                          {recruiterInfo.first_name?.[0] || 'R'}{recruiterInfo.last_name?.[0] || ''}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg sm:text-xl font-bold text-slate-800 truncate">
                        {recruiterInfo.first_name} {recruiterInfo.last_name}
                      </h4>
                    </div>
                  </div>
                  
                  {recruiterInfo.company_name && (
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                      <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1">
                        <Home className="w-3.5 h-3.5 text-pink-500" />
                        Company
                      </label>
                      <p className="text-sm font-medium text-slate-800">{recruiterInfo.company_name}</p>
                    </div>
                  )}
                  
                  {recruiterInfo.recruiter_background && (
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                      <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1">
                        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                        Background & Experience
                      </label>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {recruiterInfo.recruiter_background}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 px-2">
                  <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-pink-500 mx-auto mb-2 sm:mb-3"></div>
                  <p className="text-xs sm:text-sm text-slate-500 leading-snug">Loading recruiter information...</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Reactor passport viewer (opened from a Spark) */}
      {viewingPassportCvId && (
        <div
          className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-3 sm:p-6"
          onClick={() => setViewingPassportCvId(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col"
            style={{ height: 'calc(100vh - 120px)', maxHeight: '90vh', minHeight: '460px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <PassportViewer
              cvId={viewingPassportCvId}
              audience="explorer"
              showContact={false}
              onBack={() => setViewingPassportCvId(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
